/**
 * RecommendationsInboxPage - View received recommendations
 *
 * Phase 4.3 - Recommendations
 * Phase 4.5 - Taste alignment trust tier display
 * Phase 4.6 - Explicit decision CTAs (Watched, Rate, Not interested)
 * Phase 4.7 - Persistent recommendations (never removed), filter toggle, updated handled logic
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare, User, Check, Eye, Loader2, Film, Star, ThumbsDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useUserLibrary } from '../contexts/UserLibraryContext';
import { useToast } from '../components/ui/Toast';
import { RatingModal } from '../components/RatingModal';
import { useBackNavigation } from '../hooks/useBackNavigation';
import { formatDistanceToNow } from '../utils/dateUtils';
import type { MediaItem } from '../types/movie';

// API base: either worker origin (e.g. https://...workers.dev) or '/api' for dev proxy.
// Worker normalizes paths by stripping leading /api before route matching.
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface RecommendationUser {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
}

interface Recommendation {
  id: string;
  media_id: number;
  media_type: 'movie' | 'tv';
  media_title: string;
  media_poster: string | null;
  media_year: number | null;
  message: string | null;
  is_read: boolean;
  is_dismissed: boolean;
  read_at: string | null;
  created_at: string;
  from_user: RecommendationUser | null;
}

interface AlignedFollower {
  follower_id: string;
  alignment_score: number | null;
  trust_tier: 'high' | 'medium' | 'low' | 'unknown';
}

export function RecommendationsInboxPage() {
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const { user, session } = useAuth();
  const { isWatched, getRating, toggleWatched } = useUserLibrary();
  const { toast } = useToast();

  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Filter mode: show only items needing action or show all (Phase 4.7)
  const [filterMode, setFilterMode] = useState<'needs_action' | 'all'>('needs_action');

  // Rating modal state (Phase 4.6)
  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [ratingModalMedia, setRatingModalMedia] = useState<MediaItem | null>(null);

  // Local rating overrides for instant feedback (rating reflects without page refresh)
  const [ratedOverrides, setRatedOverrides] = useState<Record<string, number>>({});

  // Taste alignment data (Phase 4.5)
  const [alignmentData, setAlignmentData] = useState<AlignedFollower[]>([]);

  // Build O(1) lookup map for taste alignment
  const alignmentMap = useMemo(() => {
    const map = new Map<string, { trust_tier: string; alignment_score: number | null }>();
    for (const item of alignmentData) {
      map.set(item.follower_id, {
        trust_tier: item.trust_tier,
        alignment_score: item.alignment_score,
      });
    }
    return map;
  }, [alignmentData]);

  // Fetch taste alignment once on mount (Phase 4.5)
  useEffect(() => {
    if (!session?.access_token) return;

    const fetchAlignment = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/taste-alignment`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });

        if (response.ok) {
          const data = await response.json();
          setAlignmentData(data.aligned_followers || []);
        }
      } catch (err) {
        // Silently fail - alignment is enhancement, not critical
        console.error('[TasteAlignment] Failed to fetch:', err);
      }
    };

    fetchAlignment();
  }, [session?.access_token]);

  // Fetch recommendations
  const fetchRecommendations = useCallback(
    async (cursor?: string) => {
      if (!session?.access_token) return;

      if (cursor) {
        setLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      try {
        const params = new URLSearchParams({ limit: '20' });
        if (cursor) {
          params.set('cursor', cursor);
        }

        const response = await fetch(`${API_BASE_URL}/recommendations/inbox?${params}`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (cursor) {
          setRecommendations((prev) => [...prev, ...data.recommendations]);
        } else {
          setRecommendations(data.recommendations || []);
        }

        setNextCursor(data.next_cursor);
        setHasMore(data.has_more || false);
      } catch (err) {
        console.error('Failed to fetch recommendations:', err);
      } finally {
        setIsLoading(false);
        setLoadingMore(false);
      }
    },
    [session?.access_token]
  );

  // Initial fetch - runs ONLY on mount (no refetch on action side effects)
  useEffect(() => {
    if (user && session?.access_token) {
      fetchRecommendations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only fetch on mount
  }, []);

  // Mark as read
  const handleMarkRead = async (id: string) => {
    if (!session?.access_token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/recommendations/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ is_read: true }),
      });

      if (response.ok) {
        setRecommendations((prev) =>
          prev.map((r) => (r.id === id ? { ...r, is_read: true } : r))
        );
      }
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  // Dismiss recommendation (Phase 4.7: optimistic update, no removal)
  const handleDismiss = async (id: string) => {
    if (!session?.access_token) return;

    // Optimistically update is_dismissed to true
    setRecommendations((prev) =>
      prev.map((r) => (r.id === id ? { ...r, is_dismissed: true } : r))
    );

    try {
      const response = await fetch(`${API_BASE_URL}/recommendations/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ is_dismissed: true }),
      });

      if (!response.ok) {
        // Rollback on failure
        setRecommendations((prev) =>
          prev.map((r) => (r.id === id ? { ...r, is_dismissed: false } : r))
        );
        toast.error('Failed to dismiss');
      }
    } catch (err) {
      console.error('Failed to dismiss:', err);
      // Rollback on error
      setRecommendations((prev) =>
        prev.map((r) => (r.id === id ? { ...r, is_dismissed: false } : r))
      );
      toast.error('Something went wrong');
    }
  };

  // View media
  const handleView = (rec: Recommendation) => {
    handleMarkRead(rec.id);
    navigate(`/${rec.media_type}/${rec.media_id}`);
  };

  // Mark as watched (Phase 4.6)
  const handleWatched = async (rec: Recommendation) => {
    const wasWatched = isWatched(rec.media_id, rec.media_type);
    try {
      const result = await toggleWatched(
        rec.media_id,
        rec.media_type,
        rec.media_title,
        rec.media_poster ?? undefined,
        rec.media_year ?? undefined
      );
      // result is null if superseded - ignore silently
      if (result) {
        toast.success(wasWatched ? 'Removed from Watched' : 'Marked as watched');
      }
    } catch (err) {
      console.error('Error toggling watched:', err);
      toast.error('Something went wrong');
    }
  };

  // Open rating modal (Phase 4.6)
  const handleRate = (rec: Recommendation) => {
    // Construct minimal MediaItem for RatingModal (only uses id, title, poster_path, backdrop_path, release_date, media_type)
    const mediaItem: MediaItem = {
      id: rec.media_id,
      title: rec.media_title,
      poster_path: rec.media_poster,
      backdrop_path: null,
      release_date: rec.media_year ? `${rec.media_year}-01-01` : '',
      media_type: rec.media_type,
      // Required but unused by RatingModal
      overview: '',
      genre_ids: [],
      vote_average: 0,
      vote_count: 0,
      popularity: 0,
      original_language: '',
    };
    setRatingModalMedia(mediaItem);
    setRatingModalOpen(true);
  };

  // Close rating modal
  const handleCloseRatingModal = () => {
    setRatingModalOpen(false);
    setRatingModalMedia(null);
  };

  // Handle rating completed (updates local override for instant feedback)
  const handleRated = (rating: number) => {
    if (ratingModalMedia) {
      const key = `${ratingModalMedia.media_type}:${ratingModalMedia.id}`;
      setRatedOverrides((prev) => ({ ...prev, [key]: rating }));
    }
  };

  // Load more
  const handleLoadMore = () => {
    if (nextCursor && !loadingMore) {
      fetchRecommendations(nextCursor);
    }
  };

  // Redirect if not logged in
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <button
            onClick={goBack}
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-3"
          >
            <ArrowLeft size={16} />
            <span className="text-sm font-medium">Back</span>
          </button>
          <p className="text-slate-400">Please sign in to view recommendations</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16">
      {/* Header */}
      <div className="bg-[#0F1419] border-b border-white/[0.06] sticky top-16 z-20">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={goBack}
                className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-2"
                aria-label="Go back"
              >
                <ArrowLeft size={18} />
                <span className="text-sm font-medium">Back</span>
              </button>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <MessageSquare size={24} className="text-orange-400" />
                Recommendations
              </h1>
              <p className="text-sm text-slate-400 mt-1">
                Movies and shows recommended by people you follow
              </p>
            </div>

            {/* Filter toggle (Phase 4.7) */}
            <div className="flex items-center gap-1 bg-white/[0.04] rounded-lg p-1">
              <button
                onClick={() => setFilterMode('needs_action')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  filterMode === 'needs_action'
                    ? 'bg-teal-500 text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Needs action
              </button>
              <button
                onClick={() => setFilterMode('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  filterMode === 'all'
                    ? 'bg-teal-500 text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-slate-500" />
          </div>
        ) : recommendations.length === 0 ? (
          <div className="text-center py-12">
            <Film size={48} className="mx-auto mb-4 text-slate-600" />
            <h2 className="text-lg font-medium text-slate-300 mb-2">
              No recommendations yet
            </h2>
            <p className="text-sm text-slate-500">
              When people you follow recommend movies or shows, they'll appear here
            </p>
          </div>
        ) : (
          <ul className="space-y-4">
            {recommendations
              .filter((rec) => {
                // Phase 4.7: Filter based on filterMode
                if (filterMode === 'all') return true;
                // needs_action: show only if NOT handled
                const recIsWatched = isWatched(rec.media_id, rec.media_type);
                const ratingKey = `${rec.media_type}:${rec.media_id}`;
                const recIsRated = (ratedOverrides[ratingKey] ?? getRating(rec.media_id, rec.media_type)) !== null;
                const recIsNotInterested = rec.is_dismissed;
                // handled = not interested OR (watched AND rated)
                const handled = recIsNotInterested || (recIsWatched && recIsRated);
                return !handled;
              })
              .map((rec) => {
              // Phase 4.7: Determine if recommendation needs action
              // handled = not interested OR (watched AND rated)
              const recIsWatched = isWatched(rec.media_id, rec.media_type);
              const ratingKey = `${rec.media_type}:${rec.media_id}`;
              const localRating = ratedOverrides[ratingKey] ?? getRating(rec.media_id, rec.media_type);
              const recIsRated = localRating !== null;
              const recIsNotInterested = rec.is_dismissed;
              const handled = recIsNotInterested || (recIsWatched && recIsRated);
              const needsAction = !handled;

              return (
                <li
                  key={rec.id}
                  className={`bg-[#1A1F26] rounded-xl ring-1 ring-white/[0.08] overflow-hidden ${
                    !rec.is_read ? 'ring-teal-500/30' : ''
                  }`}
                >
                  {/* Sender info */}
                  <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.06]">
                    <Link
                      to={rec.from_user?.username ? `/u/${rec.from_user.username}` : '#'}
                      className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity"
                    >
                      <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {rec.from_user?.avatar_url ? (
                          <img
                            src={rec.from_user.avatar_url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <User size={16} className="text-slate-400" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {rec.from_user?.display_name || rec.from_user?.username || 'Unknown'}
                        </p>
                        <p className="text-xs text-slate-500">
                          {formatDistanceToNow(rec.created_at)}
                        </p>
                      </div>
                    </Link>

                    {/* Trust tier badge (Phase 4.5) - show only for high tier */}
                    {rec.from_user?.id && alignmentMap.get(rec.from_user.id)?.trust_tier === 'high' && (
                      <span className="flex-shrink-0 flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-amber-400 bg-amber-500/10 rounded-full">
                        <Star size={12} className="fill-current" />
                        Great taste match
                      </span>
                    )}

                    {/* Action needed badge (Phase 4.6) */}
                    {needsAction ? (
                      <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium text-orange-400 bg-orange-500/10 rounded-full">
                        Action needed
                      </span>
                    ) : (
                      <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium text-slate-500 bg-white/[0.04] rounded-full">
                        Handled
                      </span>
                    )}

                    {/* Unread indicator */}
                    {!rec.is_read && (
                      <span className="flex-shrink-0 w-2 h-2 bg-teal-400 rounded-full" />
                    )}
                  </div>

                  {/* Media card */}
                  <div className="flex gap-4 p-4">
                    {/* Poster */}
                    <button
                      onClick={() => handleView(rec)}
                      className="flex-shrink-0 w-20 h-30 bg-slate-800 rounded-lg overflow-hidden hover:opacity-80 transition-opacity"
                    >
                      {rec.media_poster ? (
                        <img
                          src={`https://image.tmdb.org/t/p/w154${rec.media_poster}`}
                          alt={rec.media_title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Film size={24} className="text-slate-600" />
                        </div>
                      )}
                    </button>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => handleView(rec)}
                        className="text-left hover:opacity-80 transition-opacity"
                      >
                        <h3 className="text-base font-medium text-white line-clamp-2">
                          {rec.media_title}
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {rec.media_type === 'tv' ? 'TV Show' : 'Movie'}
                          {rec.media_year && ` • ${rec.media_year}`}
                        </p>
                      </button>

                      {/* Message */}
                      {rec.message && (
                        <p className="text-sm text-slate-300 mt-2 line-clamp-3 italic">
                          "{rec.message}"
                        </p>
                      )}

                      {/* Actions (Phase 4.6: Watched, Rate, Not interested, View) */}
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        {/* Watched button */}
                        {!recIsWatched ? (
                          <button
                            onClick={() => handleWatched(rec)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-teal-500 rounded-lg hover:bg-teal-400 transition-colors"
                          >
                            <Check size={14} />
                            Mark watched
                          </button>
                        ) : (
                          <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-teal-400 bg-teal-500/10 rounded-lg">
                            <Check size={14} />
                            Watched
                          </span>
                        )}

                        {/* Rate button */}
                        {!recIsRated ? (
                          <button
                            onClick={() => handleRate(rec)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-300 hover:text-white bg-white/[0.06] rounded-lg hover:bg-white/[0.1] transition-colors"
                          >
                            <Star size={14} />
                            Rate
                          </button>
                        ) : (
                          <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-yellow-400 bg-yellow-500/10 rounded-lg">
                            <Star size={14} className="fill-current" />
                            {localRating}/10
                          </span>
                        )}

                        {/* Not interested button - Phase 4.7: active state when dismissed */}
                        {!recIsNotInterested ? (
                          <button
                            onClick={() => handleDismiss(rec.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-400 hover:text-red-400 bg-white/[0.06] rounded-lg hover:bg-red-500/10 transition-colors"
                          >
                            <ThumbsDown size={14} />
                            Not interested
                          </button>
                        ) : (
                          <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-400 bg-red-500/10 rounded-lg">
                            <ThumbsDown size={14} />
                            Not interested
                          </span>
                        )}

                        {/* View button (tertiary) */}
                        <button
                          onClick={() => handleView(rec)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-400 hover:text-slate-300 transition-colors"
                        >
                          <Eye size={14} />
                          View
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* Load more */}
        {hasMore && (
          <div className="mt-6 text-center">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="px-6 py-2 text-sm font-medium text-teal-400 hover:text-teal-300 disabled:opacity-50 transition-colors"
            >
              {loadingMore ? (
                <Loader2 size={16} className="mx-auto animate-spin" />
              ) : (
                'Load more'
              )}
            </button>
          </div>
        )}
      </div>

      {/* Rating Modal (Phase 4.6) */}
      {ratingModalMedia && (
        <RatingModal
          movie={ratingModalMedia}
          isOpen={ratingModalOpen}
          onClose={handleCloseRatingModal}
          onRated={handleRated}
        />
      )}
    </div>
  );
}
